import { forgotPasswordBodySchema, type ActionResult } from '@flux/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { forgotPassword } from './api.js';

type ForgotPasswordValues = { email: string };

/**
 * `02` §1 : « réponse identique que l'email existe ou non ». Le texte affiché
 * après envoi est `ActionResult.message` tel que renvoyé par l'API — jamais
 * reformulé ici, pour ne jamais diverger du contrat serveur.
 */
export function ForgotPasswordForm(): JSX.Element {
  const [result, setResult] = useState<ActionResult | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordValues>({ resolver: zodResolver(forgotPasswordBodySchema) });

  const mutation = useMutation({
    mutationFn: (values: ForgotPasswordValues) => forgotPassword(values.email),
    onSuccess: setResult,
    // Volontairement identique en erreur réseau : on ne distingue pas côté UI
    // un compte inconnu d'un échec technique (`02` §1).
    onError: () => {
      setResult({
        ok: true,
        message: 'Si un compte existe avec cet email, un lien vient de lui être envoyé.',
      });
    },
  });

  if (result) {
    return (
      <div className="flex flex-col gap-4">
        <p className="rounded-md bg-muted px-3 py-2.5 text-sm text-foreground">{result.message}</p>
        <Link
          to="/connexion"
          className="self-center rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          Retour à la connexion
        </Link>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event) => {
        void handleSubmit((values) => {
          mutation.mutate(values);
        })(event);
      }}
    >
      <Field label="Email" htmlFor="email" error={errors.email?.message}>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          aria-invalid={errors.email ? true : undefined}
          {...register('email')}
        />
      </Field>
      <Button type="submit" className="w-full" isLoading={isSubmitting || mutation.isPending}>
        Envoyer le lien
      </Button>
      <Link
        to="/connexion"
        className="self-center rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        Retour à la connexion
      </Link>
    </form>
  );
}
